import { CompanySaleModificationReportDto, CompanySaleTicketReportDto, CompanySaleTicketReportResponse, SellStationSummaryReport } from '@/__generated';
import AppDatePicker from '@/components/FormItems/DatePicker';
import { dateTimeDisplayFormat, formatApiDate, ROW_GUTTER } from '@/config';
import { formatCurrency } from '@/helpers/format';
import { TripJourneyType } from '@/constants';
import { apiSdk } from '@/httpClient';
import MainContent from '@/layouts/MainContent';
import { Button, Col, Form, Pagination, Row, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { t } from 'i18next';
import moment from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import CountryInput from '@/components/FormItems/CountryInput';
import SellerInput from '@/components/FormItems/SellerInput';
import { ExportExcelBtn, ExportPdfBtn } from '@/components/app/ExportBtn';
import { exportService } from '@/services/exportService';

type Props = {};
const PAGE_TITLE = 'Sales Report';
interface SaleReportRowProps extends CompanySaleTicketReportDto {
  isRowHeader?: boolean;
  isSummaryField?: boolean;
  childData?: SellStationSummaryReport[];
  totalTicketAmount?: number;
  isBlankField?: boolean;
}
const PageSize = 20;
const SalesReportPage = (props: Props) => {
  const [searchForm] = Form.useForm();
  const [tableKey, setTableKey] = React.useState<number>();
  const [report, setReport] = React.useState<CompanySaleTicketReportResponse>();
  const [loading, setIsLoading] = React.useState(false);
  const [filter, setFilter] = React.useState<{ fromDate: string; toDate: string; pageIndex: number; countryId?: string; registerId?: string; cashierId?: string }>({
    fromDate: formatApiDate(moment().add(-1, 'month')),
    toDate: formatApiDate(moment()),
    pageIndex: 1,
  });
  React.useEffect(() => {
    setIsLoading(true);

    apiSdk.CompanySaleReportService.ticketSaleReports({
      ...filter,
      fromDate: filter?.fromDate,
      toDate: filter?.toDate,
      pageSize: PageSize,
    })
      .then(res => {
        setReport(res);
        setTableKey(new Date().getTime());
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  const data = React.useMemo(() => {
    if (!report?.data?.length) {
      return [];
    }
    let { data, sellStations } = report;
    let reportData: SaleReportRowProps[] = [];

    sellStations?.forEach(station => {
      reportData.push({
        bookingRefNo: t('salesReport_StationGroup', { station: station.stationName, count: station.totalRecords }),
        isRowHeader: true,
        childData: data,
      });
      reportData.push({
        isSummaryField: true,
        ticketAmount: station.ticketAmount,
        discountAmount: station.discountAmount,
        cancelledAmount: station.cancelledAmount,
        outStandingAmount: station.outStandingAmount,
      });
    });

    reportData.push({ isBlankField: true, isRowHeader: true });
    reportData.push({
      isSummaryField: true,
      ticketAmount: report.totalTicketAmount,
      discountAmount: report.totalDiscountAmount,
      cancelledAmount: report.totalCancelledAmount,
      outStandingAmount: report.totalOutStandingAmount,
    });

    return reportData;
  }, [report]);

  const columns = React.useMemo(() => {
    let cols: ColumnsType<SaleReportRowProps> = [];
    cols.push({
      title: t('Sales Report Ticket Details'),
      ellipsis: true,
      children: [
        {
          width: 170,
          title: t('Booking Ref'),
          ellipsis: true,
          dataIndex: 'bookingRefNo',
          render: (v, record) => (record.isRowHeader ? <b>{v}</b> : v),
          onCell: r => ({ colSpan: r.isRowHeader ? 12 : r.isSummaryField ? 8 : 1 }),
        },
        {
          title: t('Date'),
          ellipsis: true,
          width: 160,
          dataIndex: 'createdOn',
          render: v => dateTimeDisplayFormat.dateTime(v),
          onCell: r => ({ colSpan: r.isRowHeader || r.isSummaryField ? 0 : 1 }),
        },
      ],
    });
    cols.push({
      title: t('Date Departure'),
      ellipsis: true,
      width: 160,
      dataIndex: 'departureDate',
      render: v => dateTimeDisplayFormat.dateTime(v),
      onCell: r => ({ colSpan: r.isRowHeader || r.isSummaryField ? 0 : 1 }),
    });
    cols.push({
      title: t('Sales Report Ticket Details'),
      ellipsis: true,
      children: [
        {
          title: t('Sell station'),
          ellipsis: true,
          width: 120,
          dataIndex: 'sellStationName',
          onCell: r => ({ colSpan: r.isRowHeader || r.isSummaryField ? 0 : 1 }),
        },
        // Caisse et caissier (2026-11-02) : deux caissiers d'une même gare se distinguent.
        {
          title: t('seller_Register'),
          ellipsis: true,
          width: 110,
          dataIndex: 'registerName',
          onCell: r => ({ colSpan: r.isRowHeader || r.isSummaryField ? 0 : 1 }),
        },
        {
          title: t('seller_Cashier'),
          width: 150,
          dataIndex: 'cashierName',
          onCell: r => ({ colSpan: r.isRowHeader || r.isSummaryField ? 0 : 1 }),
        },
        {
          title: t('Destination'),
          dataIndex: 'destinationName',
          width: 120,
          ellipsis: true,
          onCell: r => ({ colSpan: r.isRowHeader || r.isSummaryField ? 0 : 1 }),
        },

        {
          title: t('Ticket type'),
          dataIndex: 'journeyType',
          width: 120,
          ellipsis: true,
          render: v => TripJourneyType.__getLabel(v),
          onCell: r => ({ colSpan: r.isRowHeader || r.isSummaryField ? 0 : 1 }),
        },
        {
          title: t('Amount'),
          ellipsis: true,
          dataIndex: 'ticketAmount',
          width: 150,

          render: (v, record) => {
            if (record.isSummaryField) {
              return <b>{`Total: ${formatCurrency(v)}`}</b>;
            }
            return formatCurrency(v);
          },
          onCell: r => ({ colSpan: r.isRowHeader ? 0 : 1 }),
        },
        {
          ellipsis: true,
          title: t('Discount Amount'),
          dataIndex: 'discountAmount',
          width: 150,

          render: (v, record) => {
            if (record.isSummaryField) {
              return <b>{`Total: ${formatCurrency(v)}`}</b>;
            }
            return formatCurrency(v);
          },
          onCell: r => ({ colSpan: r.isRowHeader ? 0 : 1 }),
        },
        {
          ellipsis: true,
          title: t('Cancelled Amount'),
          dataIndex: 'cancelledAmount',
          width: 150,

          render: (v, record) => {
            if (record.isSummaryField) {
              return <b>{`Total: ${formatCurrency(v)}`}</b>;
            }
            return formatCurrency(v);
          },
          onCell: r => ({ colSpan: r.isRowHeader ? 0 : 1 }),
        },
        {
          ellipsis: true,

          title: t('OutStanding Amount'),
          dataIndex: 'outStandingAmount',
          width: 160,
          render: (v, record) => {
            if (record.isSummaryField) {
              return <b>{`Total: ${formatCurrency(v)}`}</b>;
            }
            return formatCurrency(v);
          },
          onCell: r => ({ colSpan: r.isRowHeader ? 0 : 1 }),
        },
      ],
    });

    return cols;
  }, []);

  const onTableChange = nextPageIndex => {
    setFilter(x => ({ ...x, pageIndex: nextPageIndex || 1 } as any));
  };

  const onSearch = ({ fromDate, toDate, ...props }: any) => {
    setFilter({
      fromDate: formatApiDate(fromDate),
      toDate: formatApiDate(toDate),
      pageIndex: 1,
      ...props,
    });
  };

  const onExportExcel = () => {
    searchForm.validateFields().then(res => {
      exportService.exportSaleReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.toDate) }).toExcel();
    });
  };
  const onExportPdf = () => {
    searchForm.validateFields().then(res => {
      exportService.exportSaleReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.toDate) }).toPdf();
    });
  };

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        <Space>
          <ExportPdfBtn onClick={onExportPdf} />
          <ExportExcelBtn onClick={onExportExcel} />
        </Space>
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Form
          layout="inline"
          className="nta-custom-form"
          form={searchForm}
          colon={false}
          onFinish={onSearch}
          initialValues={filter}
          style={{ marginTop: 10 }}
        >
          <Row gutter={ROW_GUTTER}>
            <Col md={24} xl={24} xxl={24}>
              <Space>
                <Form.Item name="fromDate" label={t('From date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item name="toDate" label={t('To Date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item name="countryId" label={t('Country')}>
                  <CountryInput style={{ width: 200 }} />
                </Form.Item>
                <Form.Item name="registerId" label={t('seller_Register')}>
                  <SellerInput kind="register" style={{ width: 220 }} />
                </Form.Item>
                <Form.Item name="cashierId" label={t('seller_Cashier')}>
                  <SellerInput kind="cashier" style={{ width: 200 }} />
                </Form.Item>
                <Form.Item style={{ textAlign: 'left' }}>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined />
                      {t('zTable.search')}
                    </Button>
                    <Button type="ghost" htmlType="reset">
                      <CloseOutlined />
                      {t('zTable.reset')}
                    </Button>
                  </Space>
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Form>

        <Table
          key={tableKey}
          className="transport-report-table"
          loading={loading}
          size="small"
          bordered
          columns={columns}
          expandable={{
            rowExpandable: record => !!record.isRowHeader,
            fixed: 'left',
            defaultExpandAllRows: true,
            childrenColumnName: 'childData',
          }}
          dataSource={data}
          pagination={false}
          rowClassName={v => (v.isSummaryField ? 'summary-row' : '')}
        />
        {/* Modifications de billets de la période (2026-11-02) : leur différence compte à la date de la modification. */}
        <div style={{ marginTop: 16, paddingLeft: 8 }} data-testid="sales-report-modifications">
          <b>
            {t('salesReport_Modifications')} ({report?.modifications?.length || 0})
          </b>
          <Table<CompanySaleModificationReportDto>
            rowKey="modificationId"
            size="small"
            bordered
            pagination={false}
            style={{ marginTop: 8 }}
            locale={{ emptyText: t('salesReport_NoModification') }}
            dataSource={report?.modifications || []}
            columns={[
              { title: t('salesReport_Date'), dataIndex: 'createdOn', width: 150, render: v => dateTimeDisplayFormat.dateTime(v) },
              { title: t('salesReport_Booking'), dataIndex: 'bookingRefNo', width: 170, ellipsis: true },
              { title: t('salesReport_Tickets'), width: 260, render: (_, r) => `${r.oldTicketRefNo || ''} → ${r.newTicketRefNo || ''}` },
              { title: t('salesReport_Station'), dataIndex: 'sellStationName', ellipsis: true },
              { title: t('seller_Register'), dataIndex: 'registerName', width: 110 },
              { title: t('seller_Cashier'), dataIndex: 'cashierName', width: 150 },
              { title: t('salesReport_Settlement'), dataIndex: 'settlement', width: 150, render: v => t(`salesReport_Settlement${v}`) },
              { title: t('salesReport_Difference'), dataIndex: 'difference', width: 130, render: v => formatCurrency(v) },
            ]}
          />
          <div style={{ marginTop: 8 }} data-testid="sales-report-totals">
            {t('salesReport_TotalSales')} : <b>{formatCurrency(report?.totalTicketAmount)}</b> — {t('salesReport_TotalModifications')} :{' '}
            <b>{formatCurrency(report?.totalModificationAmount)}</b> — {t('salesReport_TotalOutstanding')} : <b>{formatCurrency(report?.totalOutStandingAmount)}</b>
          </div>
        </div>
        <div style={{ textAlign: 'end', marginTop: 10, marginBottom: 10 }}>
          <Pagination
            hideOnSinglePage={true}
            showSizeChanger={false}
            onChange={onTableChange}
            current={filter?.pageIndex || 1}
            pageSize={PageSize}
            total={report?.total}
            showTotal={total => <div>{`Total: ${total}`}</div>}
            size="default"
          />
        </div>
      </div>
    </MainContent>
  );
};
export default SalesReportPage;
